import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, RefreshCw, ScanBarcode, Smartphone, UserRound } from 'lucide-react';
import TopBar from '../components/TopBar';
import Loader from '../components/Loader';
import LineChart from '../components/LineChart';
import { api, errorMessage } from '../lib/api';
import { cn, qty } from '../lib/utils';

const PERIODS = [7, 30, 90, 365];

const when = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return 'just now';
  if (diff < 60) return `${Math.round(diff)} min ago`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)} h ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const dateLabel = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const Card = ({ title, aside, children }) => (
  <section className="mt-3 rounded-xl bg-surface px-3 py-3">
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-[13px] font-semibold text-ink2">{title}</h2>
      {aside && <span className="shrink-0 text-[12px] text-ink2">{aside}</span>}
    </div>
    {children}
  </section>
);

/* A stat tile: label, value, an optional meter (segments in status tones,
   2px gaps, on a lighter track of the same ramp) and a one-line caption
   that says in words what the meter shows, so nothing rides on colour alone. */
const Tile = ({ label, value, meter, caption }) => (
  <div className="min-w-0 rounded-xl bg-surface px-3 py-2.5">
    <p className="text-[12px] text-ink2">{label}</p>
    <p className="font-cond mt-0.5 text-[26px] font-bold leading-none">{value}</p>
    {meter && (
      <span className="mt-2 flex h-1.5 w-full gap-0.5 overflow-hidden rounded-[3px] bg-sunken">
        {meter.filter((m) => m.share > 0).map((m) => (
          <span key={m.key} className={cn('block h-full', m.className)} style={{ width: `${m.share * 100}%` }} />
        ))}
      </span>
    )}
    {caption && <p className="mt-1 text-[11px] leading-tight text-ink2">{caption}</p>}
  </div>
);

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

function Person({ p, max }) {
  const [open, setOpen] = useState(false);
  const share = max > 0 ? Math.max(0, Math.min(1, p.units / max)) : 0;
  return (
    <li>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 py-2 text-left">
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', p.active_now ? 'tone-out bg-tone-bg text-tone' : 'bg-sunken text-ink2')}>
          <UserRound className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[15px] font-semibold">{p.name}</span>
              {p.active_now && <span className="h-2 w-2 shrink-0 rounded-full bg-out" aria-label="active now" />}
            </span>
            <span className="shrink-0 text-[12px] text-ink2">
              <span className="font-semibold text-ink">{qty(p.units)}</span> units · {p.receipts} {p.receipts === 1 ? 'receipt' : 'receipts'}
            </span>
          </span>
          <span className="mt-1 block h-2 w-full overflow-hidden rounded-[4px] bg-out-bg" aria-hidden="true">
            <span className="block h-full rounded-r-[4px] bg-out transition-[width] duration-300" style={{ width: `${share * 100}%` }} />
          </span>
          <span className="mt-1 block truncate text-[12px] text-ink2">
            last seen {when(p.last_seen)} · {p.active_days} {p.active_days === 1 ? 'day' : 'days'} receiving · {p.sessions} {p.sessions === 1 ? 'sign-in' : 'sign-ins'}
          </span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 shrink-0 text-ink2" /> : <ChevronDown className="h-4 w-4 shrink-0 text-ink2" />}
      </button>
      {open && (
        <div className="mb-2 grid grid-cols-3 gap-2 rounded-xl bg-sunken px-3 py-2 text-[13px]">
          <span><span className="font-semibold">{p.lines}</span> <span className="text-ink2">lines</span></span>
          <span><span className="font-semibold">{qty(p.avg_units)}</span> <span className="text-ink2">units per receipt</span></span>
          <span><span className="font-semibold">{p.scans}</span> <span className="text-ink2">scans</span></span>
          <span><span className={cn('font-semibold', p.unknown_scans > 0 && 'text-hold')}>{p.unknown_scans}</span> <span className="text-ink2">unknown</span></span>
          <span><span className={cn('font-semibold', p.failed > 0 && 'text-alert')}>{p.failed}</span> <span className="text-ink2">failed</span></span>
          <span><span className="font-semibold">{when(p.last_receipt)}</span> <span className="text-ink2">last receipt</span></span>
          <span className="col-span-3 text-ink2">{p.login}{p.first_seen ? ` · first sign-in ${dateLabel(p.first_seen)}` : ''}</span>
        </div>
      )}
    </li>
  );
}

function Day({ d }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 py-1.5 text-left text-[14px]">
        <span className="text-ink2">{dayLabel(d.day)}</span>
        <span className="text-right">
          <span className="font-semibold">{d.receipts}</span> {d.receipts === 1 ? 'receipt' : 'receipts'} · {qty(d.units)} units · {d.people} {d.people === 1 ? 'person' : 'people'}
        </span>
      </button>
      {open && (
        <ul className="mb-1 rounded-xl bg-sunken px-3 py-1.5 text-[13px]">
          {d.by_user.map((u) => (
            <li key={u.name} className="flex justify-between py-0.5">
              <span>{u.name}</span>
              <span className="text-ink2">
                {u.receipts} {u.receipts === 1 ? 'receipt' : 'receipts'} · {qty(u.units)} units
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allDays, setAllDays] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setData(await api.adminUsage(days));
    } catch (err) {
      setError(err?.response?.status === 403 ? 'This screen is for admins.' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const [measure, setMeasure] = useState('units'); // units | receipts
  const perDay = data?.receipts?.per_day || [];
  const shownDays = allDays ? perDay : perDay.slice(0, 14);
  // Every day of the period, zero-filled, oldest first.
  const series = useMemo(() => {
    if (!data) return [];
    const byDay = Object.fromEntries(perDay.map((d) => [d.day, d]));
    const out = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = data.days - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const row = byDay[key];
      out.push({
        key,
        label: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
        tick: d.toLocaleDateString('en-GB', data.days > 60 ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short' }),
        value: row ? (measure === 'units' ? row.units : row.receipts) : 0,
        detail: row
          ? [
              { label: measure === 'units' ? (row.receipts === 1 ? 'receipt' : 'receipts') : 'units', value: measure === 'units' ? row.receipts : qty(row.units) },
              { label: row.people === 1 ? 'person' : 'people', value: row.people },
            ]
          : [{ label: 'nothing received', value: '' }],
      });
    }
    return out;
  }, [data, perDay, measure]);

  return (
    <div className="min-h-screen pb-10">
      <TopBar
        title="Usage"
        subtitle="Who signs in, what gets received"
        onBack={() => navigate('/')}
        right={
          <button type="button" onClick={load} disabled={busy} aria-label="Refresh" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
            <RefreshCw className={cn('h-5 w-5', busy && 'animate-spin')} />
          </button>
        }
      >
        <div className="mx-auto flex max-w-lg gap-1.5 px-3 pb-2" role="tablist" aria-label="Period">
          {PERIODS.map((p) => (
            <button key={p} type="button" role="tab" aria-selected={days === p} onClick={() => setDays(p)} className={cn('h-10 rounded-full px-3 text-[13px] font-semibold', days === p ? 'bg-ink text-bg' : 'bg-surface text-ink2 active:bg-sunken')}>
              {p === 365 ? '1 year' : `${p} days`}
            </button>
          ))}
        </div>
      </TopBar>

      <main className="mx-auto max-w-lg px-3">
        {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
        {!data && !error && <Loader label="Counting…" />}
        {data && (
          <>
            {/* One hero number leads: what came in during the period, with its trend. */}
            <Card title={`Received in the last ${data.days} days`} aside={`updated ${when(data.generated_at)}`}>
              <div className="mt-1 flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-cond text-[52px] font-bold leading-none">{qty(data.receipts.units)}</p>
                  <p className="mt-1 text-[13px] text-ink2">
                    units on {data.receipts.lines} {data.receipts.lines === 1 ? 'line' : 'lines'} · {data.receipts.count} {data.receipts.count === 1 ? 'receipt' : 'receipts'}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1.5" role="tablist" aria-label="Measure">
                  {[
                    ['units', 'Units'],
                    ['receipts', 'Receipts'],
                  ].map(([m, label]) => (
                    <button key={m} type="button" role="tab" aria-selected={measure === m} onClick={() => setMeasure(m)} className={cn('h-8 rounded-full px-3 text-[12px] font-semibold', measure === m ? 'bg-ink text-bg' : 'bg-sunken text-ink2 active:bg-line')}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <LineChart points={series} unit={measure} className="mt-2" />
              {data.receipts.busiest_day && (
                <p className="mt-1 text-[13px] text-ink2">
                  Busiest day: <span className="font-semibold text-ink">{dayLabel(data.receipts.busiest_day.day)}</span>, {qty(data.receipts.busiest_day.units)} units in {data.receipts.busiest_day.receipts} {data.receipts.busiest_day.receipts === 1 ? 'receipt' : 'receipts'}.
                </p>
              )}
              {perDay.length === 0 && <p className="mt-2 text-[14px] text-ink2">No receipts confirmed in this period.</p>}
            </Card>

            {/* Three tiles, each with a meter that says how healthy the number is. */}
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Tile
                label="Receipts"
                value={data.receipts.count}
                meter={[
                  { key: 'ok', share: pct(data.receipts.count - data.receipts.failed, data.receipts.count) / 100, className: 'bg-out' },
                  { key: 'failed', share: pct(data.receipts.failed, data.receipts.count) / 100, className: 'bg-alert' },
                ]}
                caption={data.receipts.count === 0 ? 'none yet' : data.receipts.failed ? `${data.receipts.failed} failed · ${pct(data.receipts.count - data.receipts.failed, data.receipts.count)}% validated` : 'all validated'}
              />
              <Tile
                label="People"
                value={data.people.length}
                meter={[{ key: 'active', share: data.people.length ? data.active_now / data.people.length : 0, className: 'bg-out' }]}
                caption={data.active_now ? `${data.active_now} active in the last 15 min` : 'nobody active right now'}
              />
              <Tile
                label="Scans"
                value={data.scans.total}
                meter={[
                  { key: 'matched', share: pct(data.scans.matched, data.scans.total) / 100, className: 'bg-out' },
                  { key: 'unknown', share: pct(data.scans.unknown, data.scans.total) / 100, className: 'bg-hold' },
                ]}
                caption={data.scans.total ? `${pct(data.scans.matched, data.scans.total)}% matched a line · ${data.scans.unknown} unknown` : 'stored with receipts only'}
              />
            </div>

            <Card title="Who received what" aside={`${data.days} days · bars compare units · tap for detail`}>
              {data.people.length === 0 && <p className="mt-2 text-[14px] text-ink2">Nobody signed in during this period.</p>}
              <ul className="mt-1 divide-y divide-line">
                {[...data.people]
                  .sort((a, b) => b.units - a.units)
                  .map((p) => (
                    <Person key={p.login} p={p} max={Math.max(...data.people.map((x) => x.units), 0)} />
                  ))}
              </ul>
            </Card>

            <Card title="Day by day" aside={`${data.receipts.count} receipts · ${qty(data.receipts.units)} units`}>
              <ul className="mt-1 divide-y divide-line">
                {shownDays.map((d) => (
                  <Day key={d.day} d={d} />
                ))}
                {perDay.length === 0 && <li className="py-2 text-[14px] text-ink2">Nothing yet.</li>}
              </ul>
              {perDay.length > 14 && (
                <button type="button" onClick={() => setAllDays((v) => !v)} className="mt-2 w-full rounded-xl bg-sunken py-2 text-[13px] font-semibold text-ink2 active:bg-line">
                  {allDays ? 'Show the last 14 days' : `Show all ${perDay.length} days`}
                </button>
              )}
            </Card>

            <Card title="All time" aside={data.all_time.since ? `since ${dateLabel(data.all_time.since)}` : ''}>
              <p className="mt-1 text-[13px] leading-relaxed text-ink2">
                <span className="font-semibold text-ink">{qty(data.all_time.units)}</span> units on <span className="font-semibold text-ink">{data.all_time.lines}</span> lines in{' '}
                <span className="font-semibold text-ink">{data.all_time.receipts}</span> receipts · <span className="font-semibold text-ink">{data.all_time.people}</span> people ·{' '}
                <span className="font-semibold text-ink">{data.all_time.sessions}</span> sign-ins · <span className="font-semibold text-ink">{data.all_time.scans}</span> scans
                {data.all_time.unknown_scans ? <>, {data.all_time.unknown_scans} unknown</> : null}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-[12px] text-ink2">
                <ScanBarcode className="h-3.5 w-3.5 shrink-0" /> Scans are only stored with a confirmed receipt; nothing else is logged.
              </p>
            </Card>

            <Card title="Latest receipts">
              <ul className="mt-1 divide-y divide-line">
                {data.receipts.recent.map((r, i) => (
                  <li key={`${r.po}-${i}`} className="py-2 text-[14px]">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="font-cond truncate text-[18px] font-bold leading-none">{r.po}</span>
                      <span className="flex shrink-0 items-center gap-2 text-[12px] text-ink2">
                        {when(r.at)}
                        {r.report_url && (
                          <a href={r.report_url} target="_blank" rel="noreferrer" className="font-semibold text-ink underline-offset-2 hover:underline">
                            PDF
                          </a>
                        )}
                      </span>
                    </span>
                    <span className="block truncate text-[13px] text-ink2">
                      {r.supplier} · {qty(r.units)} units on {r.lines} {r.lines === 1 ? 'line' : 'lines'}{r.by && ` · ${r.by}`}
                      {r.status !== 'validated' && <span className="text-alert"> · {r.status}</span>}
                    </span>
                  </li>
                ))}
                {data.receipts.recent.length === 0 && <li className="py-2 text-[14px] text-ink2">Nothing yet.</li>}
              </ul>
            </Card>

            <Card title="Latest sign-ins">
              <ul className="mt-1 divide-y divide-line">
                {data.recent_sessions.map((s, i) => (
                  <li key={`${s.login}-${s.at}-${i}`} className="flex items-center gap-3 py-2 text-[14px]">
                    <Smartphone className="h-4 w-4 shrink-0 text-ink2" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{s.name}</span>
                      <span className="block text-[12px] text-ink2">
                        {s.device} · {when(s.at)}
                        {s.active ? ' · active' : s.ended ? ' · signed out' : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
